"use client";

import { useCallback, useState } from "react";
import { AutoFallbackImage } from "@/components/ui/auto-fallback-image";
import {
  ProjectDetailsDonationWidget,
  type ShareImagePayload,
} from "@/components/projects/ProjectDetailsDonationWidget";
import { normalizeNbsp } from "@/lib/html/normalizeNbsp";
import type { Project, ProjectShare } from "@/types";

interface GiftDetailShowcaseProps {
  project: Project;
  locale: string;
  isRTL: boolean;
  defaultImage: ShareImagePayload;
  detailsHtml: string | null;
  /** Server-rendered shares seeded into the donation widget. */
  initialShares?: ProjectShare[];
}

/**
 * Gift detail layout: main image (left) + donation widget / details HTML (right).
 * Share selection in the widget drives the main image.
 */
export function GiftDetailShowcase({
  project,
  locale,
  isRTL,
  defaultImage,
  detailsHtml,
  initialShares,
}: GiftDetailShowcaseProps) {
  const defaultImageUrl = defaultImage.imageUrl;
  const defaultImageId = defaultImage.imageId;

  const [activeImage, setActiveImage] =
    useState<ShareImagePayload>(defaultImage);

  const handleShareImageChange = useCallback(
    (image: ShareImagePayload | null) => {
      setActiveImage(
        image ?? { imageUrl: defaultImageUrl, imageId: defaultImageId },
      );
    },
    [defaultImageUrl, defaultImageId],
  );

  return (
    <div className="row grid grid-cols-1 lg:grid-cols-12 gap-6 mt-2">
      {/* Main image */}
      <div className="min-w-0 lg:col-span-5">
        <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-muted">
          <AutoFallbackImage
            key={`${activeImage.imageUrl ?? ""}-${activeImage.imageId ?? ""}`}
            imageUrl={activeImage.imageUrl ?? null}
            imageId={activeImage.imageId}
            alt={project.Name}
            fill
            className="object-contain"
            priority
          />
        </div>
      </div>

      {/* Donation widget + details */}
      <div
        className="min-w-0 space-y-6 lg:col-span-7"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {project.ShortDescription && (
          <div className="border rounded-lg bg-white p-4 text-center">
            <div className="break-words text-info font-bold text-sm [overflow-wrap:anywhere]">
              {normalizeNbsp(project.ShortDescription)}
            </div>
            <div className="mt-4">
              <ProjectDetailsDonationWidget
                project={project}
                locale={locale}
                onShareImageChange={handleShareImageChange}
                initialShares={initialShares}
              />
            </div>
          </div>
        )}

        {!project.ShortDescription && (
          <ProjectDetailsDonationWidget
            project={project}
            locale={locale}
            onShareImageChange={handleShareImageChange}
            initialShares={initialShares}
          />
        )}

        {detailsHtml && (
          <div className="min-w-0 overflow-hidden rounded-xl border bg-white p-4 sm:p-5">
            <div
              className="prose prose-sm max-w-none break-words text-foreground prose-p:leading-relaxed prose-headings:text-foreground prose-li:marker:text-muted-foreground [overflow-wrap:anywhere]"
              dangerouslySetInnerHTML={{ __html: normalizeNbsp(detailsHtml) }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
